import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import api from '../../services/api';
import { useWorkspace } from '../../utils/WorkspaceContext';
import useDebounce from '../../hooks/debounceHook';

/**
 * @typedef {Object} DivisionFormData
 * @property {string} name - Division name
 * @property {string} description - Division description
 * @property {string} department - Department code (parent)
 * @property {number} [workspace] - Workspace ID (added on submit)
 */

/**
 * @typedef {Object} CreateDivisionDialogProps
 * @property {string} [department] - Pre-selected department code
 * @property {Object} [division] - Existing division to edit (omit to create)
 * @property {boolean} open - Whether the dialog is open
 * @property {function} onClose - Function to call when dialog should close
 * @property {function} onSuccess - Function to call when the division is saved
 */

/**
 * Dialog component for creating and editing divisions.
 * @component
 * @param {CreateDivisionDialogProps} props - Component props
 * @returns {React.ReactElement} The division dialog
 */
const DivisionDialog = ({ department, division, open, onClose, onSuccess }) => {
  const isEdit = Boolean(division?.id);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    department: department || '',
  });
  const [departments, setDepartments] = useState([]);
  const { currentWorkspace } = useWorkspace();

  // Load the workspace's department entities for the picker.
  useEffect(() => {
    if (!open || !currentWorkspace?.id) return;
    api
      .get('departments/', { params: { workspace_id: currentWorkspace.id } })
      .then((res) => setDepartments(res.data))
      .catch((err) => console.error('Error fetching departments:', err));
  }, [open, currentWorkspace?.id]);

  // Sync form state whenever the dialog is (re)opened for a new target.
  useEffect(() => {
    if (!open) return;
    setFormData({
      name: division?.name || '',
      description: division?.description || '',
      department: division?.department || department || '',
    });
  }, [open, division, department]);

  const debouncedSaveDivision = useDebounce(async (divisionData) => {
    try {
      if (isEdit) {
        await api.patch(`divisions/${division.id}/`, divisionData);
      } else {
        await api.post('divisions/', divisionData);
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving division:', error);
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    debouncedSaveDivision({ ...formData, workspace: currentWorkspace.id });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Division' : 'Create New Division'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Division Name"
            fullWidth
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
          <TextField
            select
            margin="dense"
            label="Department"
            fullWidth
            required
            value={formData.department}
            onChange={(e) =>
              setFormData({ ...formData, department: e.target.value })
            }
          >
            {departments.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DivisionDialog;
